/**
 * Servizio per l'autenticazione con API Auting
 */

import { setAuthenticatedUser } from './growthbook.js';
import { featureFlags } from '$lib/stores/feature-flags.js';
import { getApiConfig } from '$lib/config/environment.js';
import { apiFetch } from '$lib/services/api-fetch.js';
import { extractApiErrorMessage } from '$lib/services/api-error.js';

// Usa la configurazione dinamica dell'API
const getApiBaseUrl = () => getApiConfig().baseUrl;

/**
 * Effettua il login con email e password
 * @param {string} username - Email dell'utente
 * @param {string} password - Password dell'utente
 * @returns {Promise<{access_token: string}>}
 */
export async function login(username, password) {
  const API_BASE_URL = getApiBaseUrl();
  
  const response = await apiFetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      username,
      password
    })
  });

  // Leggi il corpo di errore in modo sicuro se la risposta non è ok
  if (!response.ok) {
    let errorMessage = response.statusText;
    try {
      const errorContentType = response.headers.get('content-type') || '';
      if (errorContentType.includes('application/json')) {
        const errorData = await response.json().catch(() => null);
        if (errorData) {
          errorMessage = extractApiErrorMessage(errorData) || JSON.stringify(errorData);
        }
      } else {
        const text = await response.text().catch(() => null);
        if (text) {
          errorMessage = text;
        }
      }
    } catch (e) {
      console.warn('Unable to parse error response body', e);
    }
    throw new Error(`Login failed: ${errorMessage} (${response.status})`);
  }

  // Controlla se la risposta è in formato JSON
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    throw new Error(`Unexpected response format: ${contentType}`);
  }

  const result = await response.json();
  return result;
}

/**
 * Effettua la registrazione con nome, cognome, email e password
 * @param {string} firstName - Nome dell'utente
 * @param {string} lastName - Cognome dell'utente
 * @param {string} email - Email dell'utente
 * @param {string} password - Password dell'utente
 * @param {string} recaptchaToken - Token reCAPTCHA
 * @param {string} language - Lingua dell'utente
 * @returns {Promise<{access_token: string}>}
 */
export async function register(firstName, lastName, email, password, recaptchaToken, language = 'it') {
  const API_BASE_URL = getApiBaseUrl();
  
  const response = await apiFetch(`${API_BASE_URL}/auth/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
      password,
      nome: firstName,
      cognome: lastName,
      flagTos: true,
      urlConfermaRegistrazione: "https://auting.it/confermaMailHelper/",
      lingua: language,
      tipoLogin: 1,
      tipoUtente: 1,
      flagPrivacy: true,
      token: recaptchaToken
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = extractApiErrorMessage(errorData) || `Registration failed: ${response.status} ${response.statusText}`;
    throw new Error(errorMessage);
  }

  // La registrazione può rispondere 201 con body vuoto: è il flusso atteso quando
  // non viene restituito un access_token (conferma via email) oppure quando il
  // backend applica il tarpit anti-abuso. Non assumere che ci sia sempre del JSON,
  // altrimenti response.json() lancia "JSON.parse: unexpected end of data".
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return {};
  }

  const result = await response.json().catch(() => ({}));
  return result;
}

/**
 * Richiede il reset della password inviando un'email di recupero
 * @param {string} email - Email dell'utente per il reset password
 * @returns {Promise<{message: string}>}
 */
export async function resetPassword(email) {
  const API_BASE_URL = getApiBaseUrl();
  
  const response = await apiFetch(`${API_BASE_URL}/auth/pass-recovery`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = extractApiErrorMessage(errorData) || `Password reset failed: ${response.status} ${response.statusText}`;
    throw new Error(errorMessage);
  }

  return true;
}

/**
 * Reindirizza alla pagina di login Facebook
 */
export function loginWithFacebook() {
  const API_BASE_URL = getApiBaseUrl();
  const facebookLoginUrl = `${API_BASE_URL}/auth/fb/web-login`;
  window.location.href = facebookLoginUrl;
}

/**
 * Reindirizza alla pagina di login Apple
 */
export function loginWithApple() {
  const API_BASE_URL = getApiBaseUrl();
  const appleLoginUrl = `${API_BASE_URL}/auth/apple/web-login`;
  window.location.href = appleLoginUrl;
}


export async function authenticated(user, accessToken) {
  console.log('authenticated() chiamata con:', { user: user?.abUuid ? { ...user, abUuid: user.abUuid } : user, hasToken: !!accessToken });

  if (!user || !accessToken) {
    throw new Error('User and access token are required for authentication check');
  }
  
  // Controlla se l'utente ha un UUID di AB dal profilo
  if (user.abUuid) {
    console.debug('L\'utente ha un AB UUID nel profilo:', user.abUuid);
    // Imposta l'utente autenticato in GrowthBook con l'UUID esistente
  setAuthenticatedUser(user.id, user.abUuid);
  await featureFlags.refresh(true);
    return true;
  }

  // Controlla se esiste un cookie gbuuid dalla sessione precedente
  const existingGbUuid = document.cookie.split('; ').find(row => row.startsWith('gbuuid='))?.split('=')[1];
  console.debug('Cookie gbuuid esistente:', existingGbUuid);

  if (existingGbUuid) {
    console.log('Trovato UUID esistente nel cookie, lo associo all\'utente');
    // Imposta l'utente autenticato usando l'UUID dal cookie
  setAuthenticatedUser(user.id, existingGbUuid);
  await featureFlags.refresh(true);
    
    // Chiama l'API per associare l'UUID al profilo utente se necessario
    try {
      const API_BASE_URL = getApiBaseUrl();
      const authHeader = `Bearer ${accessToken}`;
      console.debug('Associando AB UUID al profilo utente:', existingGbUuid);

      const response = await apiFetch(`${API_BASE_URL}/auth/authenticated`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({
          abUuid: existingGbUuid
        })
      });

      if (!response.ok) {
        console.warn('Impossibile associare l\'UUID al profilo utente:', response.status);
      } else {
        console.debug('UUID associato al profilo utente con successo');
      }
    } catch (error) {
      console.warn('Errore durante l\'associazione dell\'UUID:', error);
    }
    
    return true;
  }

  // Se non c'è nessun UUID, GrowthBook ne genererà uno nuovo automaticamente
  console.log('Nessun UUID esistente, GrowthBook genererà un nuovo UUID');
  setAuthenticatedUser(user.id);
  await featureFlags.refresh(true);
  
  return true;
}


/**
 * Decodifica un JWT token
 * @param {string} token - Il JWT token da decodificare
 * @returns {import('../types/auth.js').DecodedToken}
 */
export function tokenToUser(token) {
  try {
    // Decodifica base del JWT senza verifica della firma
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    throw new Error('Invalid token format');
  }
}

/**
 * Utility per impostare un cookie in modo sicuro
 * @param {string} name - Nome del cookie
 * @param {string} value - Valore del cookie
 * @param {Object} options - Opzioni per il cookie
 */
export function setCookie(name, value, options = {}) {
  const defaults = {
    expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString(), // 1 anno di default
    path: '/',
    secure: true,
    sameSite: 'Strict'
  };
  
  const cookieOptions = { ...defaults, ...options };
  
  let cookieString = `${name}=${value}`;
  
  if (cookieOptions.expires) {
    cookieString += `; expires=${cookieOptions.expires}`;
  }
  if (cookieOptions.path) {
    cookieString += `; path=${cookieOptions.path}`;
  }
  if (cookieOptions.secure) {
    cookieString += `; secure`;
  }
  if (cookieOptions.sameSite) {
    cookieString += `; SameSite=${cookieOptions.sameSite}`;
  }
  
  document.cookie = cookieString;
  
  // Debug log per verificare che il cookie sia stato impostato
  console.log(`🍪 Setting cookie ${name}:`, cookieString);
  console.log(`🌍 Environment: ${location.protocol}//${location.hostname}, isDevelopment: ${!cookieOptions.secure}`);
  
  // Verifica che il cookie sia stato effettivamente salvato
  setTimeout(() => {
    const savedCookie = document.cookie.split('; ').find(row => row.startsWith(`${name}=`));
    if (savedCookie) {
      console.log(`✅ Cookie ${name} successfully saved`);
    } else {
      console.error(`❌ Cookie ${name} was NOT saved!`);
    }
  }, 100);
  
  // Verifica immediata che il cookie sia stato impostato
  setTimeout(() => {
    const setCookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${name}=`))
      ?.split('=')[1];
    console.log(`Verifica cookie ${name}:`, setCookieValue);
  }, 100);
}

/**
 * Utility per cancellare un cookie
 * @param {string} name - Nome del cookie da cancellare
 */
export function deleteCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  console.debug(`Cookie ${name} cancellato`);
}